import { JwtSchema, StoreTiersSchema } from '../types';
/**
 * Session service
 */
class SessionService {
    storageAdapter;
    storeService;
    jwtKey = '';
    currentTiersKey = '';
    /**
     * SessionService constructor
     */
    constructor({ storageAdapter, storeService }) {
        this.storageAdapter = storageAdapter;
        this.storeService = storeService;
    }
    /**
     * Set jwtKey
     */
    async setJwtKey(jwtKey) {
        this.jwtKey = jwtKey;
    }
    /**
     * Get JWT key
     */
    getJwtKey() {
        return this.jwtKey;
    }
    /**
     * Set currentTiersKey
     */
    async setCurrentTiersKey(currentTiersKey) {
        this.currentTiersKey = currentTiersKey;
    }
    /**
     * Get Tiers reference
     */
    async getTiersReference() {
        const tiers = await this.getCurrentTiers();
        return tiers.ref;
    }
    /**
     * Get JWT Token
     */
    async getToken() {
        const jwt = await this.getJWT();
        return jwt.jwt || '';
    }
    /**
     * Get Bearer Autorization
     */
    async getBearer() {
        return `Bearer ${await this.getToken()}`;
    }
    /**
     * Check if given module is activated in the JWT payload
     */
    async isModuleActivated(module) {
        const jwt = await this.getJWT();
        return !!jwt.payload[module];
    }
    /**
     * Check if given license is active in the JWT payload
     * the license is active if the module is present and if the date has not expired
     */
    async isLicenseActive(module) {
        const jwt = await this.getJWT();
        if (jwt.payload.license?.modules) {
            const licenseModule = jwt.payload.license.modules.find((mod) => {
                return mod.href.endsWith(module);
            });
            // a license without expiration date is valid
            if (licenseModule) {
                return !licenseModule.expiration || new Date(licenseModule.expiration) >= new Date();
            }
        }
        return false;
    }
    /**
     * Return the jwt from the storage
     */
    async getJWT() {
        if (!this.jwtKey) {
            throw new Error('jwtKey must be set via setJwtKey');
        }
        const value = await this.storageAdapter.getValue(this.jwtKey);
        // we validate that the format is the right one
        const jwt = JwtSchema.parse(value);
        return jwt;
    }
    /**
     * Return the tiers from the storage
     */
    async getCurrentTiers() {
        if (!this.currentTiersKey) {
            throw new Error('[session-service] (getCurrentTiers) currentTiersKey must be set via setCurrentTiersKey');
        }
        const value = await this.storageAdapter.getValue(this.currentTiersKey);
        if (!value) {
            throw new Error(`[session-service] (getCurrentTiers) currentTiers must be set via set using key (${this.currentTiersKey})`);
        }
        // we validate that the format is the right one
        const result = StoreTiersSchema.safeParse(value);
        if (result.success) {
            return result.data;
        }
        throw new Error(`[session-service] (getCurrentTiers) Error parsing currenTiers`, result.error);
    }
    /**
     * Get JWT Token
     */
    async getCurrentUser() {
        const jwt = await this.getJWT();
        const payload = jwt.payload;
        return {
            self: payload.self ?? '',
            displayName: payload.displayName ?? '',
            email: payload.emails[0] ?? '',
            franceConnect: payload.franceConnect,
            providerId: payload.providerId,
            dateTimeLastLogin: payload.dateTimeLastLogin,
        };
    }
    /**
     * Load current user
     */
    async loadCurrentUser() {
        const currentUserStore = this.storeService.getCurrentUser();
        if (!currentUserStore.loading()) {
            currentUserStore.SET_LOADING(true);
            try {
                const currentUser = await this.getCurrentUser();
                currentUserStore.SET_DATA(currentUser);
            }
            catch (error) {
                currentUserStore.SET_ERROR(error);
            }
            currentUserStore.SET_LOADING(false);
        }
    }
    /**
     * Logout
     */
    async logout() {
        const currentUserStore = this.storeService.getCurrentUser();
        currentUserStore.SET_DATA(null);
        return this.storageAdapter.removeValue(this.jwtKey);
    }
}
export { SessionService };
//# sourceMappingURL=session-service.js.map